import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import { api, formatEUR, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Car, CalendarCheck, FileText, Banknote, ClipboardList, Settings2, Save, Euro, MapPin, Plus, Trash2, CreditCard, AlertTriangle, CheckCircle2, PenLine, Clock, Sun, Bell, GripVertical, Palette, Building2, Users, Tags, ChevronRight, ChevronDown, ArrowLeft, Sparkles, Scale, Info, X } from "lucide-react";
import SignatureCapture from "../../components/SignatureCapture";
import { useSettings, SectionHeader, SaveBar, TextField, TextArea, Toggle, VatSelector, HoursTable, InfoPopover } from "./parametrages/shared";
import { confirmAction } from "../../components/ConfirmDialog";
import { TypeIcon, ICON_PICKER, TYPE_COLOR_OPTIONS, typeColorHex, isCustomColor, DEFAULT_TYPE_ICON, DEFAULT_TYPE_COLOR } from "../../components/fleet/typeIcons";
import FleetOnboardingGuide from "../../components/fleet/FleetOnboardingGuide";
import TypeFieldsConfig from "../../components/fleet/TypeFieldsConfig";
import { fleetLabelMaps, categoryLabel } from "../../lib/vehicleLabel";
import CompanySection from "./parametrages/CompanySection";
import PricingSection from "./parametrages/PricingSection";
import ProductsSection from "./parametrages/ProductsSection";
import SetupWizard from "./parametrages/SetupWizard";
import LocationSettingsPanel from "./parametrages/LocationSettingsPanel";
import LegalSection from "./parametrages/LegalSection";
import TeamSection from "./parametrages/TeamSection";
import RolesSection from "./parametrages/RolesSection";
import { SecuritySection, ActivitySection } from "./parametrages/SecurityActivity";
import { Shield, KeyRound, ScrollText, UserCog, Search, Mail } from "lucide-react";
import EmailsSection from "./parametrages/EmailsSection";
import PaymentsSection from "./parametrages/PaymentsSection";
import { usePermissions } from "../../lib/permissions";
import { AccessDenied } from "../../components/AccessDenied";

const UNIVERSES = [
  {
    key: "entreprise", label: "Entreprise", icon: Building2,
    desc: "Identité, logo, signature & tampon, agences, textes juridiques, emails",
    subs: [
      { key: "company", label: "Identité", icon: Building2 },
      { key: "signature", label: "Signature & tampon", icon: PenLine },
      { key: "locations", label: "Agences & lieux", icon: MapPin },
      { key: "legal", label: "Juridique", icon: Scale },
      { key: "emails", label: "Emails", icon: Mail },
    ],
  },
  {
    key: "tarification", label: "Tarification", icon: Tags,
    desc: "Grille tarifaire : prix par catégorie, saison et durée de location",
    subs: [
      { key: "pricing", label: "Grille tarifaire", icon: Euro },
      { key: "products", label: "Options", icon: Tags },
    ],
  },
  {
    key: "reservations", label: "Réservations", icon: CalendarCheck,
    desc: "Durées, horaires et règles de location",
    subs: [
      { key: "bookings", label: "Règles & horaires", icon: CalendarCheck },
    ],
  },
  {
    key: "flotte", label: "Flotte", icon: Car,
    desc: "Configurez vos types de biens, leurs catégories, les champs de vos véhicules et leurs statuts.",
    chips: ["Types de biens", "Catégories", "Champs des véhicules", "Statuts personnalisés"],
    subs: [
      { key: "fleet", label: "Flotte", icon: Car },
    ],
  },
  {
    key: "finances", label: "Finances", icon: Banknote,
    desc: "TVA, devis, paiements Stripe, coûts de revient",
    subs: [
      { key: "finance", label: "TVA & rentabilité", icon: Banknote },
      { key: "quotes", label: "Devis", icon: FileText },
      { key: "payments", label: "Paiements", icon: CreditCard },
      { key: "vehicles", label: "Coûts de revient", icon: Car },
    ],
  },
  {
    key: "crm", label: "CRM", icon: Users,
    desc: "Formulaire de réservation client & champs personnalisés",
    subs: [
      { key: "forms", label: "Formulaire client", icon: ClipboardList },
    ],
  },
  {
    key: "equipe", label: "Équipe & Sécurité", icon: Shield,
    desc: "Utilisateurs, rôles & permissions, mot de passe, journal d'activité",
    subs: [
      { key: "team", label: "Utilisateurs", icon: UserCog },
      { key: "roles", label: "Rôles & permissions", icon: Shield },
      { key: "security", label: "Sécurité", icon: KeyRound },
      { key: "activity", label: "Journal d'activité", icon: ScrollText },
    ],
  },
];

// ─── Recherche globale dans les paramètres ────────────────────────────────────
const SEARCH_KEYWORDS = {
  company: "raison sociale nom commercial siret tva intracommunautaire adresse téléphone email site logo fuseau horaire format date société entreprise identité",
  locations: "agence lieu départ retour horaires aller simple surcoût surcoûts tarifaires supplément adresse principale",
  signature: "signature tampon cachet contrat apposition",
  legal: "cgv conditions générales mentions légales contrat franchise caution km kilomètres inclus rachat inventaire edl juridique",
  emails: "email expediteur reply-to envoi historique emails confirmation devis contrat lien paiement resend modèles",
  finance: "tva iban bic banque délai paiement pénalités facture pied coûts fixes rentabilité seuil occupation devise",
  quotes: "devis validité conditions acompte email modèle tva mention",
  payments: "stripe paiement en ligne carte bancaire onboarding encaissement",
  pricing: "tarif grille saison palier prix jour simulateur haute basse",
  products: "produits options catalogue siège bébé gps extras prd",
  bookings: "réservation durée minimale maximale buffer âge permis conducteur caution sources horaires annulation modification calcul jours 24h calendaire",
  fleet: "flotte types de biens catégories champs des véhicules champs personnalisés statuts personnalisés couleurs icônes alertes documents assurance ct contrôle technique véhicule configuration",
  vehicles: "coût de revient rentabilité charges amortissement",
  forms: "formulaire client champs personnalisés crm",
  team: "utilisateurs collaborateurs équipe inviter compte membre",
  roles: "rôles permissions droits accès administrateur agent comptable",
  security: "mot de passe sécurité authentification",
  activity: "journal activité historique connexions logs ip",
};

function SettingsSearch({ onOpen }) {
  const [q, setQ] = useState("");
  const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const results = q.trim().length >= 2
    ? UNIVERSES.flatMap((u) => u.subs.map((s) => ({ ...s, universe: u.label })))
        .filter((s) => norm(`${s.label} ${s.universe} ${SEARCH_KEYWORDS[s.key] || ""}`).includes(norm(q)))
        .slice(0, 8)
    : [];
  return (
    <div className="mt-5 relative max-w-xl" data-testid="param-search">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Rechercher un réglage… (ex : TVA, caution, horaires, catégories)"
        className="w-full h-11 pl-10 pr-4 rounded-2xl border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-violet-600 outline-none shadow-sm"
        data-testid="param-search-input"
      />
      {results.length > 0 && (
        <div className="absolute z-20 mt-2 w-full rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden" data-testid="param-search-results">
          {results.map((r) => (
            <button
              key={r.key}
              onClick={() => { setQ(""); onOpen(r.key); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-violet-50 transition-colors"
              data-testid={`param-search-result-${r.key}`}
            >
              <r.icon className="h-4 w-4 text-violet-600 shrink-0" />
              <span className="text-sm font-medium text-slate-800">{r.label}</span>
              <span className="ml-auto text-[10px] text-slate-400 uppercase tracking-wider">{r.universe}</span>
            </button>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && results.length === 0 && (
        <div className="absolute z-20 mt-2 w-full rounded-2xl border border-slate-200 bg-white shadow-xl px-4 py-3 text-sm text-slate-400" data-testid="param-search-empty">
          Aucun réglage trouvé pour « {q} »
        </div>
      )}
    </div>
  );
}

function SectionRenderer({ section }) {
  switch (section) {
    case "company": return <CompanySection />;
    case "signature": return <SignatureSection />;
    case "locations": return <LocationsSection />;
    case "legal": return <LegalSection />;
    case "emails": return <EmailsSection />;
    case "pricing": return <PricingSection />;
    case "products": return <ProductsSection />;
    case "bookings": return <BookingsSection />;
    case "finance": return <FinanceSection />;
    case "quotes": return <QuotesSection />;
    case "payments": return <PaymentsSection />;
    case "vehicles": return <VehicleCostsSection />;
    case "fleet": return <FleetSection />;
    case "forms": return <FormsSection />;
    case "team": return <TeamSection />;
    case "roles": return <RolesSection />;
    case "security": return <SecuritySection />;
    case "activity": return <ActivitySection />;
    default: return null;
  }
}

const SECTION_INFO = {
  company: { title: "Identité de votre société", points: ["Renseignez le nom, la forme juridique et le SIRET : ils apparaîtront sur vos devis, contrats et factures.", "Importez votre logo — il sera apposé sur tous vos documents et votre page de vente.", "Choisissez votre format de date et votre fuseau horaire en haut à droite.", "Cliquez sur Enregistrer en bas de page pour valider."] },
  signature: { title: "Signature & tampon", points: ["Dessinez ou importez votre signature : elle sera apposée automatiquement sur vos contrats.", "Ajoutez votre tampon d'entreprise (optionnel) pour un rendu encore plus professionnel.", "Vous n'aurez plus rien à signer manuellement."] },
  locations: { title: "Agences & lieux", points: ["Ajoutez vos agences et points de livraison.", "Cliquez sur « Configurer » sur une agence pour définir ses horaires, ses règles (durées, délais) et ses options (retour dans une autre agence, surcoûts tarifaires…).", "Un champ vide = la règle globale s'applique."] },
  legal: { title: "Textes juridiques", points: ["Activez/désactivez ce qui apparaît sur le contrat : franchise, caution, CGV, états des lieux, kilométrage.", "Cliquez sur « Rédiger avec l'IA » : elle utilise vos informations d'entreprise pour préparer CGV, mentions légales et contrat.", "Vérifiez le rendu avec le bouton « Aperçu du contrat ».", "Faites relire par un professionnel du droit avant utilisation."] },
  emails: { title: "Emails", points: ["Vos emails (devis, confirmations, contrats, liens de paiement) partent au nom de votre société via le domaine sécurisé Junago.", "Personnalisez le nom d'expéditeur et l'adresse de réponse (Reply-To).", "Consultez l'historique de tous les envois en bas de page.", "Modèles personnalisables et rappels automatiques : prochaine phase."] },
  pricing: { title: "Grille tarifaire", points: ["1 · Définissez vos saisons (basse, moyenne, haute) avec leurs dates.", "2 · Créez vos paliers de durée dégressifs (1-3 jours, 4-7 jours…).", "3 · Remplissez vos prix €/jour par catégorie dans la grille.", "La grille est la source unique des tarifs : chaque bien est facturé selon sa catégorie, la saison et la durée.", "Testez avec le simulateur en bas de page."] },
  bookings: { title: "Règles & horaires", points: ["Définissez vos horaires d'ouverture globaux (chaque agence peut les surcharger).", "Fixez les durées min/max de location, la caution et les conditions du conducteur.", "Ces règles s'appliquent à toutes vos réservations."] },
  fleet: { title: "Flotte", points: ["Personnalisez vos catégories de véhicules (utilisées par la grille tarifaire).", "Gérez les statuts et les alertes documents (assurance, contrôle technique…)."] },
  finance: { title: "TVA & rentabilité", points: ["Choisissez votre taux de TVA : il s'applique aux devis et factures.", "Renseignez vos hypothèses de coûts pour le calcul de rentabilité."] },
  quotes: { title: "Devis", points: ["Personnalisez la validité, les conditions et les mentions de vos devis."] },
  payments: { title: "Paiements", points: ["Connectez Stripe pour encaisser les réservations en ligne.", "Indispensable pour votre page de vente publique (onglet Vendre)."] },
  vehicles: { title: "Coûts de revient", points: ["Renseignez les coûts de chaque véhicule (financement, assurance, entretien…) pour connaître votre marge réelle."] },
  forms: { title: "Formulaire client", points: ["Choisissez les champs demandés à vos clients lors de la réservation.", "Ajoutez des champs personnalisés selon votre activité."] },
  team: { title: "Utilisateurs", points: ["Invitez vos collaborateurs : un mot de passe provisoire est généré, communiquez-le-leur.", "Attribuez un rôle à chacun — il détermine ce qu'ils peuvent voir et faire.", "Désactivez un compte à tout moment : l'accès est coupé immédiatement.", "Le compte propriétaire est indélébile et garde tous les droits."] },
  roles: { title: "Rôles & permissions", points: ["6 rôles prédéfinis (Administrateur, Responsable, Agent, Préparateur, Comptable, Commercial), tous modifiables et duplicables.", "Chaque rôle définit, module par module : lecture, création, modification, suppression, export, validation et accès financier.", "Les profils Agent et Préparateur ne voient pas les montants par défaut.", "Les vérifications sont faites côté serveur : un utilisateur sans permission est bloqué même s'il connaît l'URL."] },
  security: { title: "Sécurité", points: ["Changez votre mot de passe (8 caractères minimum).", "2FA, sessions ouvertes et déconnexion à distance arriveront dans une prochaine phase."] },
  activity: { title: "Journal d'activité", points: ["Qui a fait quoi, quand et depuis quelle adresse IP.", "Couvre les connexions et la gestion de l'équipe — sera étendu aux actions métier (résas, factures, contrats) en Phase 2."] },
};

function computeCompletion(settings, signatureInfo) {
  const company = settings?.company || {};
  const rates = settings?.pricing?.rates || {};
  const hasRates = Object.values(rates).some((cat) => Object.values(cat || {}).some((season) => Object.values(season || {}).some((p) => Number(p) > 0)));
  const checks = {
    company: !!(company.name && company.registration_number),
    logo: !!company.logo_data_url,
    signature: !!(signatureInfo && signatureInfo.signature_data_url),
    pricing: hasRates,
  };
  const done = Object.values(checks).filter(Boolean).length;
  return { checks, percent: Math.round(((done + 2) / 6) * 100) };
}

function universeBadge(key, checks, extra = {}) {
  if (key === "entreprise") {
    const n = (checks.company ? 1 : 0) + (checks.signature ? 1 : 0);
    if (n === 2) return { label: "Complet", cls: "bg-emerald-100 text-emerald-700" };
    if (n === 1) return { label: "Partiel", cls: "bg-amber-100 text-amber-700" };
    return { label: "À configurer", cls: "bg-red-100 text-red-700" };
  }
  if (key === "tarification") {
    return checks.pricing ? { label: "Complet", cls: "bg-emerald-100 text-emerald-700" } : { label: "À configurer", cls: "bg-red-100 text-red-700" };
  }
  if (key === "flotte") {
    return extra.fleet ? { label: "Complet", cls: "bg-emerald-100 text-emerald-700" } : { label: "À configurer", cls: "bg-red-100 text-red-700" };
  }
  return { label: "Pré-configuré", cls: "bg-slate-100 text-slate-500" };
}

const MISSING_LABELS = { company: "Identité entreprise", logo: "Logo", signature: "Signature", pricing: "Grille tarifaire" };
const MISSING_SECTION = { company: "company", logo: "company", signature: "signature", pricing: "pricing" };

// Retours contextuels réutilisables : clé ?from=<module> → écran opérationnel d'origine
const FROM_TARGETS = {
  fleet: { label: "Retour à Flotte", to: "/vendor/pilotage?tab=vehicles" },
};

function SubSectionNav({ subs, section, sectionConfigured, onSelect }) {
  const [open, setOpen] = useState(false);
  const current = subs.find((s) => s.key === section) || subs[0];
  return (
    <div className="mb-6">
      {/* Mobile : sélecteur déroulant compact */}
      <div className="lg:hidden relative">
        <button onClick={() => setOpen((o) => !o)} className="w-full inline-flex items-center gap-2 px-4 h-11 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-slate-800" data-testid="param-subnav-dropdown">
          {current?.icon && <current.icon className="h-4 w-4 text-slate-500" />}
          <span className="flex-1 text-left truncate">{current?.label}</span>
          {sectionConfigured[current?.key] && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
            <div className="absolute z-30 mt-1 left-0 right-0 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden max-h-[60vh] overflow-y-auto">
              {subs.map((s) => (
                <button key={s.key} onClick={() => { onSelect(s.key); setOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-left ${section === s.key ? "bg-violet-50 text-violet-700" : "text-slate-700 hover:bg-slate-50"}`} data-testid={`param-subnav-item-${s.key}`}>
                  <s.icon className="h-4 w-4" /> <span className="flex-1">{s.label}</span>
                  {sectionConfigured[s.key] && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      {/* Desktop : pastilles */}
      <div className="hidden lg:flex gap-1.5 overflow-x-auto no-scrollbar">
        {subs.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              onClick={() => onSelect(s.key)}
              className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-violet-400"}`}
              data-testid={`param-section-${s.key}`}
            >
              <s.icon className="h-3.5 w-3.5" /> {s.label}
              {sectionConfigured[s.key] && <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-400" : "bg-emerald-400/60"}`} data-testid={`param-dot-${s.key}`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}


export default function VendorParametrages() {
  const { can, isOwner, loaded } = usePermissions();
  const permsDenied = loaded && !isOwner && !can("settings", "read");
  const [searchParams, setSearchParams] = useSearchParams();
  const section = searchParams.get("section");
  const from = searchParams.get("from");
  const universe = section ? UNIVERSES.find((u) => u.subs.some((s) => s.key === section)) : null;
  const { settings, loading, reload } = useSettings();
  const [signatureInfo, setSignatureInfo] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const wizardShownRef = useRef(false);

  const [stripeOnboarded, setStripeOnboarded] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const refetchSignature = () => {
    api.get("/vendor/profile/signature").then(({ data }) => setSignatureInfo(data)).catch(() => setSignatureInfo({}));
  };
  useEffect(refetchSignature, []);
  useEffect(() => {
    api.get("/stripe/connect/status").then(({ data }) => setStripeOnboarded(!!data.stripe_onboarded)).catch(() => {});
  }, []);

  useEffect(() => {
    // Ne s'ouvre qu'UNE fois par visite, et jamais par-dessus une section ouverte
    if (!loading && !section && settings?.setup && settings.setup.completed !== true &&
        !sessionStorage.getItem("jn_setup_dismissed") && !wizardShownRef.current) {
      wizardShownRef.current = true;
      setWizardOpen(true);
    }
  }, [loading, settings, section]);

  const openSection = (key) => setSearchParams(from ? { section: key, from } : { section: key });
  const comp = computeCompletion(settings, signatureInfo);
  const fleetConfigured = Array.isArray(settings?.fleet?.vehicle_types) && settings.fleet.vehicle_types.length > 0 && Array.isArray(settings?.fleet?.categories) && settings.fleet.categories.length > 0;
  const [progressHidden, setProgressHidden] = useState(() => localStorage.getItem("jn_param_progress_hidden") === "1");
  const hideProgress = () => { localStorage.setItem("jn_param_progress_hidden", "1"); setProgressHidden(true); };
  const showProgress = () => { localStorage.removeItem("jn_param_progress_hidden"); setProgressHidden(false); };
  const missing = Object.entries(comp.checks).filter(([, v]) => !v).map(([k]) => k);
  const lg = settings?.legal || {};
  const sectionConfigured = {
    company: !!settings?.company?.name,
    signature: !!signatureInfo?.signature_data_url,
    locations: true,
    legal: !!(lg.cgv || lg.mentions || lg.contract_text),
    pricing: comp.checks.pricing,
    bookings: true, fleet: true, finance: true, quotes: true, forms: true, vehicles: true,
    payments: stripeOnboarded,
    emails: true,
    team: true, roles: true, security: true, activity: true,
  };

  return (
    <VendorLayout>
      {permsDenied ? (
        <AccessDenied module="aux paramétrages" />
      ) : universe ? (
        <div className="min-h-screen bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 lg:py-10">
            {from && FROM_TARGETS[from] ? (
              <Link to={FROM_TARGETS[from].to} className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-900 transition-colors mb-4" data-testid="param-contextual-back">
                <ArrowLeft className="h-3.5 w-3.5" /> {FROM_TARGETS[from].label}
              </Link>
            ) : (
              <button onClick={() => setSearchParams({})} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-violet-700 transition-colors mb-4" data-testid="param-back-to-hub">
                <ArrowLeft className="h-3.5 w-3.5" /> Paramétrages
              </button>
            )}
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
                <universe.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">{universe.label}</h1>
                  <button
                    onClick={() => setInfoOpen(true)}
                    className="h-5 w-5 rounded-full border border-slate-300 text-slate-400 hover:border-violet-400 hover:text-violet-600 flex items-center justify-center transition-colors"
                    data-testid="section-info-btn"
                    aria-label="Aide sur cette page"
                  >
                    <Info className="h-3 w-3" />
                  </button>
                </div>
                <div className="text-xs text-slate-500">{universe.desc}</div>
              </div>
            </div>
            {(() => {
              const done = universe.subs.filter((s) => sectionConfigured[s.key]).length;
              const pct = Math.round((done / universe.subs.length) * 100);
              const todo = universe.subs.filter((s) => !sectionConfigured[s.key]);
              return (
                <div className="mb-5" data-testid="universe-progress">
                  <div className="h-[3px] rounded-full bg-slate-200/70 overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-400/70 transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                  {todo.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {todo.map((s) => (
                        <button key={s.key} onClick={() => openSection(s.key)} className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full bg-amber-50 border border-amber-200 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 transition-colors" data-testid={`universe-todo-${s.key}`}>
                          Terminer : {s.label} <ChevronRight className="h-3 w-3" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {universe.subs.length > 1 && (
              <SubSectionNav subs={universe.subs} section={section} sectionConfigured={sectionConfigured} onSelect={openSection} />
            )}
            <div data-testid={`param-content-${section}`}>
              <SectionRenderer section={section} />
            </div>
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-5 lg:py-10" data-testid="param-hub">
            <div className="overline">Configuration</div>
            <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1">Paramétrages</h1>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">Tout ce qui personnalise votre espace Junago : votre entreprise, vos tarifs, vos règles de location et vos documents.</p>

            {/* Recherche globale dans les paramètres */}
            <SettingsSearch onOpen={openSection} />

            {/* Progress card (masquable) */}
            {progressHidden ? (
              <button onClick={showProgress} className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-violet-600 transition-colors" data-testid="param-progress-show">
                <Sparkles className="h-3.5 w-3.5" /> Afficher la configuration guidée
              </button>
            ) : (
            <div className="mt-6 relative rounded-2xl bg-slate-900 text-white p-5 sm:p-6" data-testid="param-progress-card">
              <button onClick={hideProgress} className="absolute top-3 right-3 h-7 w-7 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="param-progress-hide" aria-label="Masquer la configuration guidée">
                <X className="h-4 w-4" />
              </button>
              <div className="flex flex-wrap items-center justify-between gap-4 pr-8">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-violet-300" />
                    <div className="text-sm font-semibold">Configuration de votre espace</div>
                    <div className="ml-auto sm:ml-0 text-sm font-bold text-violet-300" data-testid="param-progress-percent">{loading ? "…" : `${comp.percent} %`}</div>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-700" style={{ width: `${loading ? 0 : comp.percent}%` }} />
                  </div>
                  {!loading && missing.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">À faire :</span>
                      {missing.map((k) => (
                        <button key={k} onClick={() => openSection(MISSING_SECTION[k])} className="px-2 py-0.5 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-medium text-slate-200 transition-colors" data-testid={`param-missing-${k}`}>
                          {MISSING_LABELS[k]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setWizardOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold transition-colors shrink-0"
                  data-testid="param-launch-wizard"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Configuration guidée
                </button>
              </div>
            </div>
            )}

            {/* Universe cards */}
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {UNIVERSES.map((u) => {
                const badge = universeBadge(u.key, comp.checks, { fleet: fleetConfigured });
                return (
                  <button
                    key={u.key}
                    onClick={() => openSection(u.subs[0].key)}
                    className="group text-left rounded-2xl border border-slate-200 bg-white p-5 hover:border-violet-300 hover:shadow-lg hover:shadow-violet-100/60 hover:-translate-y-0.5 transition-all"
                    data-testid={`param-universe-${u.key}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="h-10 w-10 rounded-xl bg-slate-100 group-hover:bg-violet-100 text-slate-700 group-hover:text-violet-700 flex items-center justify-center transition-colors">
                        <u.icon className="h-5 w-5" />
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
                    </div>
                    <div className="mt-3 font-heading text-base font-semibold text-slate-900">{u.label}</div>
                    <div className="mt-1 text-xs text-slate-500 leading-relaxed">{u.desc}</div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {(u.chips || u.subs.map((s) => s.label)).map((label) => (
                        <span key={label} className="px-2 py-0.5 rounded-full bg-slate-50 border border-slate-100 text-[10px] font-medium text-slate-500">{label}</span>
                      ))}
                    </div>
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-violet-700 opacity-0 group-hover:opacity-100 transition-opacity">
                      Ouvrir <ChevronRight className="h-3 w-3" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <InfoPopover
        title={SECTION_INFO[section]?.title || universe?.label || ""}
        points={SECTION_INFO[section]?.points || []}
        open={infoOpen && !!section}
        onClose={() => setInfoOpen(false)}
      />
      {wizardOpen && settings && (
        <SetupWizard
          settings={settings}
          onClose={() => { sessionStorage.setItem("jn_setup_dismissed", "1"); setWizardOpen(false); }}
          onFinished={() => { setWizardOpen(false); reload(); refetchSignature(); }}
        />
      )}
    </VendorLayout>
  );
}

function PlaceholderSection({ title, description }) {
  return (
    <div className="max-w-3xl">
      <div className="overline">{title}</div>
      <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1">{title}</h2>
      <p className="text-sm text-slate-500 mt-2 max-w-2xl">{description}</p>
      <div className="mt-8 p-8 rounded-2xl border-2 border-dashed border-slate-200 bg-white text-center">
        <div className="text-sm font-medium text-slate-700">Bientôt disponible</div>
        <div className="text-xs text-slate-500 mt-1">Cette section sera personnalisable dans la prochaine version.</div>
      </div>
    </div>
  );
}

function VehicleCostsSection() {
  const [vehicles, setVehicles] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/vendor/vehicles"),
      api.get("/vendor/settings").catch(() => ({ data: {} })),
    ])
      .then(([r, s]) => {
        setVehicles(r.data.vehicles || []);
        setCatMap(fleetLabelMaps(s.data?.fleet || {}).catMap);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;

  return (
    <div className="max-w-5xl">
      <div className="overline">Véhicules</div>
      <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1">Coûts de revient</h2>
      <p className="text-sm text-slate-500 mt-2 max-w-2xl">Renseignez les coûts mensuels par véhicule pour calculer automatiquement la rentabilité nette et le taux d'occupation.</p>

      <div className="mt-6 space-y-3">
        {vehicles.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">Aucun véhicule à configurer.</div>
        ) : vehicles.map((v) => <VehicleCostRow key={v.id} vehicle={v} catMap={catMap} />)}
      </div>
    </div>
  );
}

function VehicleCostRow({ vehicle, catMap = {} }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    purchase_price: vehicle.costs?.purchase_price || 0,
    monthly_financing: vehicle.costs?.monthly_financing || 0,
    monthly_insurance: vehicle.costs?.monthly_insurance || 0,
    monthly_maintenance: vehicle.costs?.monthly_maintenance || 0,
    monthly_misc: vehicle.costs?.monthly_misc || 0,
  });
  const [saving, setSaving] = useState(false);

  const monthlyTotal = Object.entries(form)
    .filter(([k]) => k !== "purchase_price")
    .reduce((s, [, v]) => s + parseFloat(v || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, parseFloat(v || 0)]));
      await api.put(`/vendor/vehicles/${vehicle.id}/costs`, payload);
      toast.success("Coûts enregistrés");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`cost-row-${vehicle.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-4 p-4 text-left">
        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <Car className="h-4 w-4 text-slate-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{vehicle.brand} {vehicle.model}</div>
          <div className="text-xs text-slate-500">{vehicle.license_plate || "—"}{categoryLabel(vehicle.category, catMap) ? ` · ${categoryLabel(vehicle.category, catMap)}` : ""}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Charge / mois</div>
          <div className="text-sm font-semibold text-slate-900">{formatEUR(monthlyTotal)}</div>
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <NumField label="Prix d'achat (€)" value={form.purchase_price} onChange={(v) => setForm({ ...form, purchase_price: v })} testid={`cost-purchase-${vehicle.id}`} />
            <NumField label="Financement / mois" value={form.monthly_financing} onChange={(v) => setForm({ ...form, monthly_financing: v })} testid={`cost-financing-${vehicle.id}`} />
            <NumField label="Assurance / mois" value={form.monthly_insurance} onChange={(v) => setForm({ ...form, monthly_insurance: v })} testid={`cost-insurance-${vehicle.id}`} />
            <NumField label="Entretien / mois" value={form.monthly_maintenance} onChange={(v) => setForm({ ...form, monthly_maintenance: v })} testid={`cost-maintenance-${vehicle.id}`} />
            <NumField label="Divers / mois" value={form.monthly_misc} onChange={(v) => setForm({ ...form, monthly_misc: v })} testid={`cost-misc-${vehicle.id}`} />
            <div className="flex flex-col justify-end">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Total mensuel</div>
              <div className="text-lg font-heading font-medium text-slate-900">{formatEUR(monthlyTotal)}</div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50" data-testid={`cost-save-${vehicle.id}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">{label}</label>
      <div className="mt-1 relative">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none pr-8"
          data-testid={testid}
        />
        <Euro className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
      </div>
    </div>
  );
}

// (Composants partagés Settings déplacés dans ./parametrages/shared.jsx)

// ─── Finance ──────────────────────────────────────────────────────────
function FinanceSection() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setLocal(settings.finance); }, [settings]);
  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.finance);
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (local.vat_rate !== settings.finance.vat_rate &&
        !window.confirm(`Confirmer le changement de TVA : ${settings.finance.vat_rate}% → ${local.vat_rate}% ?\nIl s'appliquera aux futurs devis et factures uniquement.`)) return;
    setSaving(true);
    try { await api.put("/vendor/settings", { finance: local }); toast.success("Paramètres finance enregistrés"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  return (
    <div className="max-w-3xl">
      <SectionHeader overline="Finance" title="TVA, frais fixes & rentabilité" subtitle="Vos paramètres financiers alimentent les calculs de rentabilité et l'affichage des devis/factures." />
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-6" data-testid="finance-section">
        <VatSelector value={local.vat_rate} onChange={(v) => set("vat_rate", v)} testid="finance-vat" />
        {local.vat_rate !== settings.finance.vat_rate && (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800" data-testid="finance-vat-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span><strong>Changement sensible :</strong> ce taux est la source de vérité de la TVA. Il s'appliquera aux <strong>futurs</strong> devis et factures. Les documents déjà émis ne sont jamais modifiés (montants figés).</span>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Frais fixes mensuels" type="number" value={local.fixed_monthly_costs} onChange={(v) => set("fixed_monthly_costs", v || 0)} suffix="€/mois" testid="finance-fixed-costs" hint="Assurance flotte, comptable, loyer agence…" />
          <TextField label="Seuil de rentabilité mensuel" type="number" value={local.breakeven_threshold} onChange={(v) => set("breakeven_threshold", v || 0)} suffix="€/mois" testid="finance-breakeven" hint="Objectif à atteindre pour être rentable." />
          <TextField label="Taux d'occupation cible" type="number" value={local.target_occupancy} onChange={(v) => set("target_occupancy", v || 0)} suffix="%" testid="finance-occupancy" hint="Utilisé pour projeter les revenus." />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-6 mt-6" data-testid="finance-billing-section">
        <div>
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Coordonnées bancaires & facturation</div>
          <p className="text-xs text-slate-500 mt-1">Ces informations figurent sur vos factures (règlement par virement, échéance et pénalités).</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="IBAN" value={local.iban || ""} onChange={(v) => set("iban", v)} testid="finance-iban" hint="Ex. : FR76 3000 4000 0100 0000 0000 000" />
          <TextField label="BIC / SWIFT" value={local.bic || ""} onChange={(v) => set("bic", v)} testid="finance-bic" />
          <TextField label="Banque" value={local.bank_name || ""} onChange={(v) => set("bank_name", v)} testid="finance-bank-name" />
          <TextField label="Échéance de paiement" type="number" value={local.payment_terms_days ?? 30} onChange={(v) => set("payment_terms_days", v === "" ? 30 : parseInt(v, 10) || 0)} suffix="jours" testid="finance-payment-terms" hint="Délai entre l'émission et la date d'échéance." />
        </div>
        <TextArea label="Pénalités de retard" value={local.late_penalty_text || ""} onChange={(v) => set("late_penalty_text", v)} rows={3} testid="finance-late-penalty" hint="Mention légale affichée en bas de chaque facture." />
        <TextArea label="Pied de page facture (optionnel)" value={local.invoice_footer || ""} onChange={(v) => set("invoice_footer", v)} rows={2} testid="finance-invoice-footer" hint="Texte libre : remerciements, informations complémentaires…" />
      </div>
      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="finance-save" />
    </div>
  );
}

// ─── Devis ────────────────────────────────────────────────────────────
function QuotesSection() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setLocal(settings.quotes); }, [settings]);
  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.quotes);
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/vendor/settings", { quotes: local }); toast.success("Paramètres devis enregistrés"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  return (
    <div className="max-w-3xl">
      <SectionHeader overline="Devis" title="Configuration des devis" subtitle="Ces valeurs sont appliquées par défaut à chaque nouveau devis." />
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-6" data-testid="quotes-section">
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="quotes-vat-inherit-toggle">
            <input
              type="checkbox"
              checked={local.vat_inherit !== false}
              onChange={(e) => set("vat_inherit", e.target.checked)}
              className="h-4 w-4 mt-0.5 rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              data-testid="quotes-vat-inherit"
            />
            <span className="text-sm text-slate-700">
              <span className="font-semibold">Utiliser le taux de TVA des Finances (recommandé)</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Source de vérité unique : {settings.finance?.vat_rate ?? 20}% — modifiable dans la section <span className="font-semibold">Finances</span>. Vous pourrez toujours ajuster la TVA devis par devis.
              </span>
            </span>
          </label>
          {local.vat_inherit === false && (
            <VatSelector value={local.default_vat_rate} onChange={(v) => set("default_vat_rate", v)} testid="quotes-vat" />
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Durée de validité par défaut" type="number" value={local.validity_days} onChange={(v) => set("validity_days", v || 30)} suffix="jours" testid="quotes-validity" />
        </div>
        <TextArea label="Conditions générales" value={local.conditions} onChange={(v) => set("conditions", v)} rows={4} testid="quotes-conditions" hint="Affiché en bas de chaque devis." />
        <TextArea label="Mention légale" value={local.legal_mention} onChange={(v) => set("legal_mention", v)} rows={2} testid="quotes-legal" hint="Ex. : TVA non applicable, art. 293 B du CGI." />
        <TextArea label="Modèle d'email d'envoi" value={local.email_template} onChange={(v) => set("email_template", v)} rows={5} testid="quotes-email" hint="Variables disponibles : {client_name}, {vehicle_name}, {start_date}, {end_date}." />
      </div>
      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="quotes-save" />
    </div>
  );
}

// ─── Réservations & horaires ──────────────────────────────────────────
// (DAYS + HoursTable déplacés dans ./parametrages/shared.jsx)

function BookingsSection() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setLocal(settings.bookings); }, [settings]);
  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.bookings);
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/vendor/settings", { bookings: local }); toast.success("Règles de réservation enregistrées"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  return (
    <div className="max-w-3xl pb-24">
      <SectionHeader overline="Réservations" title="Règles & horaires de location" subtitle="Contraintes appliquées à toutes les réservations de vos véhicules." />

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 space-y-6" data-testid="bookings-section">
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Durée & buffers</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Durée minimum" type="number" value={local.min_duration_days} onChange={(v) => set("min_duration_days", v || 1)} suffix="jour(s)" testid="bk-min-duration" />
            <TextField label="Durée maximum" type="number" value={local.max_duration_days} onChange={(v) => set("max_duration_days", v || 30)} suffix="jour(s)" testid="bk-max-duration" />
            <TextField label="Buffer avant réservation" type="number" value={local.buffer_before_hours} onChange={(v) => set("buffer_before_hours", v || 0)} suffix="heure(s)" testid="bk-buffer-before" hint="Temps de préparation entre deux locations." />
            <TextField label="Buffer après réservation" type="number" value={local.buffer_after_hours} onChange={(v) => set("buffer_after_hours", v || 0)} suffix="heure(s)" testid="bk-buffer-after" hint="Nettoyage, inspection." />
          </div>
        </div>

        <div className="pt-6 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Conducteur</div>
          <div className="grid sm:grid-cols-3 gap-4">
            <TextField label="Âge minimum conducteur" type="number" value={local.min_driver_age} onChange={(v) => set("min_driver_age", v || 21)} suffix="ans" testid="bk-age" />
            <TextField label="Ancienneté du permis" type="number" value={local.min_license_years} onChange={(v) => set("min_license_years", v || 2)} suffix="année(s)" testid="bk-license" />
            <TextField label="Caution par défaut" type="number" value={local.deposit_amount} onChange={(v) => set("deposit_amount", v || 0)} suffix="€" testid="bk-deposit" hint="Montant de référence. Son affichage sur les contrats se règle dans Juridique → Documents." />
          </div>
        </div>

        <div className="pt-6 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Calcul des jours & annulation</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">Mode de calcul des jours facturés</label>
              <select
                value={local.day_count_mode || "calendar"}
                onChange={(e) => set("day_count_mode", e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none"
                data-testid="bk-day-count-mode"
              >
                <option value="calendar">Jours calendaires (défaut) — du 10 au 12 = 3 jours</option>
                <option value="h24">Tranches de 24 h glissantes — du 10 au 12 = 2 jours</option>
              </select>
              <p className="text-[11px] text-slate-400 mt-1">S'applique aux nouvelles réservations et devis. Les réservations existantes ne sont pas recalculées.</p>
            </div>
            <TextField label="Délai d'annulation gratuite" type="number" value={local.cancellation_notice_hours ?? 48} onChange={(v) => set("cancellation_notice_hours", v === "" ? 0 : parseInt(v, 10) || 0)} suffix="heures avant départ" testid="bk-cancellation-notice" hint="Règle contractuelle affichée sur votre boutique. Le blocage automatique arrivera dans une prochaine phase." />
          </div>
          <div className="mt-4">
            <TextArea label="Règles de modification (optionnel)" value={local.modification_policy || ""} onChange={(v) => set("modification_policy", v)} rows={2} testid="bk-modification-policy" hint="Ex. : Modification gratuite jusqu'à 24 h avant le départ, sous réserve de disponibilité." />
          </div>
        </div>

        <div className="pt-6 border-t border-slate-100">
          <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Acompte & sources</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Acompte par défaut" type="number" value={local.deposit_percent ?? 30} onChange={(v) => set("deposit_percent", v || 0)} suffix="% du total" testid="bk-deposit-percent" hint="Proposé à chaque réservation, modifiable au cas par cas." />
            <TextField label="Sources de réservation" value={(local.sources || []).join(", ")} onChange={(v) => set("sources", v.split(",").map((s) => s.trim()).filter(Boolean))} testid="bk-sources" hint="Liste séparée par des virgules (Téléphone, Agence, Walk-in…)." />
          </div>
        </div>

        <div className="pt-6 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-3.5 w-3.5 text-slate-500" />
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Horaires standards</div>
          </div>
          <HoursTable value={local.hours_regular} onChange={(v) => set("hours_regular", v)} testid="bk-hours-regular" />
        </div>

        <div className="pt-6 border-t border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sun className="h-3.5 w-3.5 text-amber-500" />
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Horaires saisonniers</div>
            </div>
            <button type="button" onClick={() => set("summer_enabled", !local.summer_enabled)} className={`text-[10px] font-semibold px-3 py-1 rounded-full transition-colors ${local.summer_enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`} data-testid="bk-summer-toggle">
              {local.summer_enabled ? "Activé" : "Désactivé"}
            </button>
          </div>
          {local.summer_enabled && (
            <>
              <div className="grid sm:grid-cols-2 gap-4 mb-3">
                <TextField label="Début (MM-JJ)" value={local.summer_start} onChange={(v) => set("summer_start", v)} placeholder="06-01" testid="bk-summer-start" />
                <TextField label="Fin (MM-JJ)" value={local.summer_end} onChange={(v) => set("summer_end", v)} placeholder="09-30" testid="bk-summer-end" />
              </div>
              <HoursTable value={local.hours_summer} onChange={(v) => set("hours_summer", v)} testid="bk-hours-summer" />
            </>
          )}
        </div>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="bookings-save" />
    </div>
  );
}

// ─── Formulaire de réservation client ─────────────────────────────────
const FIELD_OPTIONS = [
  { key: "ask_insurance",       label: "Assurance additionnelle",  hint: "Proposer une option d'assurance premium au client" },
  { key: "ask_airport_pickup",  label: "Prise en charge aéroport", hint: "Le client peut cocher une prise en charge à l'aéroport" },
  { key: "ask_delivery_address",label: "Livraison à une adresse",  hint: "Adresse de livraison souhaitée par le client" },
  { key: "ask_flight_number",   label: "Numéro de vol",            hint: "Utile si vous proposez la prise en charge aéroport" },
  { key: "ask_gps",             label: "GPS",                       hint: "Le client peut ajouter un GPS embarqué" },
  { key: "ask_child_seat",      label: "Siège enfant",              hint: "Nombre et âge des enfants si concerné" },
  { key: "ask_secondary_driver",label: "Conducteur secondaire",     hint: "Nom & permis du 2e conducteur" },
  { key: "ask_purpose",         label: "Motif de la location",      hint: "Perso / pro / événement" },
  { key: "ask_special_requests",label: "Demandes particulières",    hint: "Zone de texte libre pour le client" },
];

function FormsSection() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (settings) setLocal(settings.form_fields); }, [settings]);
  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.form_fields);
  const set = (k, v) => setLocal((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/vendor/settings", { form_fields: local }); toast.success("Formulaire mis à jour"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  const custom = local.custom_fields || [];
  const addCustom = () => set("custom_fields", [...custom, { id: `f-${Date.now()}`, label: "Nouvelle question", type: "text", required: false }]);
  const removeCustom = (id) => set("custom_fields", custom.filter((c) => c.id !== id));
  const updateCustom = (id, patch) => set("custom_fields", custom.map((c) => c.id === id ? { ...c, ...patch } : c));

  return (
    <div className="max-w-3xl pb-24">
      <SectionHeader overline="Formulaire client" title="Champs du formulaire de réservation" subtitle="Cochez les champs à demander au client lors de sa réservation. Ajoutez vos propres questions personnalisées ci-dessous." />

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="forms-section">
        <div className="text-xs font-semibold text-slate-700 mb-3 uppercase tracking-wider">Options standards</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {FIELD_OPTIONS.map((f) => (
            <Toggle key={f.key} label={f.label} hint={f.hint} checked={!!local[f.key]} onChange={(v) => set(f.key, v)} testid={`form-toggle-${f.key}`} />
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Champs personnalisés ({custom.length})</div>
            <button type="button" onClick={addCustom} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800" data-testid="form-add-custom">
              <Plus className="h-3 w-3" /> Ajouter un champ
            </button>
          </div>
          {custom.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400">Aucun champ personnalisé.</div>
          ) : (
            <div className="space-y-2">
              {custom.map((c) => (
                <div key={c.id} className="flex items-center gap-2 p-2 rounded-xl border border-slate-200 bg-slate-50" data-testid={`form-custom-${c.id}`}>
                  <input value={c.label} onChange={(e) => updateCustom(c.id, { label: e.target.value })} className="flex-1 h-9 px-2 rounded-md border border-slate-200 bg-white text-sm" placeholder="Libellé de la question" />
                  <select value={c.type} onChange={(e) => updateCustom(c.id, { type: e.target.value })} className="h-9 px-2 rounded-md border border-slate-200 bg-white text-xs">
                    <option value="text">Texte</option>
                    <option value="number">Nombre</option>
                    <option value="boolean">Case à cocher</option>
                  </select>
                  <label className="flex items-center gap-1 text-[11px] text-slate-600">
                    <input type="checkbox" checked={!!c.required} onChange={(e) => updateCustom(c.id, { required: e.target.checked })} /> obligatoire
                  </label>
                  <button type="button" onClick={() => removeCustom(c.id)} className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="forms-save" />
    </div>
  );
}


// ─── Signature & Tampon (auto-apposés sur les contrats) ─────────────────
function SignatureSection() {
  const [signature, setSignature] = useState(null);
  const [stamp, setStamp] = useState(null);
  const [initial, setInitial] = useState({ signature: null, stamp: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/vendor/profile/signature")
      .then(({ data }) => {
        setSignature(data.signature_data_url || null);
        setStamp(data.stamp_data_url || null);
        setInitial({ signature: data.signature_data_url || null, stamp: data.stamp_data_url || null });
      })
      .finally(() => setLoading(false));
  }, []);

  const dirty = signature !== initial.signature || stamp !== initial.stamp;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/vendor/profile/signature", {
        signature_data_url: signature ?? "",
        stamp_data_url: stamp ?? "",
      });
      setInitial({ signature: data.signature_data_url || null, stamp: data.stamp_data_url || null });
      toast.success("Signature enregistrée. Elle sera automatiquement apposée sur vos prochains contrats.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;

  return (
    <div className="max-w-3xl">
      <div className="overline">Signature</div>
      <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1">Signature & tampon du loueur</h2>
      <p className="text-sm text-slate-500 mt-2 max-w-2xl">
        Votre signature (et votre tampon, optionnel) sont automatiquement apposés sur les contrats de location au nom de votre société.
        Le client signe manuellement de son côté — vous n'avez rien à faire à chaque contrat.
      </p>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="signature-section">
        <SignatureCapture
          label="Ma signature"
          value={signature}
          onChange={setSignature}
          testid="vendor-signature"
        />

        <div className="mt-6 pt-6 border-t border-slate-100">
          <SignatureCapture
            label="Mon tampon (optionnel)"
            value={stamp}
            onChange={setStamp}
            height={140}
            testid="vendor-stamp"
          />
          <div className="text-[11px] text-slate-500 mt-2">Le tampon n'est pas obligatoire. Idéalement une image PNG à fond transparent.</div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-4">
          <div className="text-xs text-slate-500">
            {initial.signature ? "Signature enregistrée sur votre profil." : "Aucune signature enregistrée."}
          </div>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="signature-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Enregistrer
          </button>
        </div>
      </div>

      <div className="mt-4 rounded-xl bg-violet-50 border border-violet-100 p-4 text-xs text-violet-800 flex gap-2">
        <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-slate-900">Comment ça marche ?</div>
          <ul className="mt-1 space-y-0.5 text-slate-700">
            <li>• Dès qu'une réservation est créée, votre signature (et tampon) sont automatiquement apposés sur le contrat.</li>
            <li>• Le client reçoit le contrat pré-signé et signe à son tour depuis son espace.</li>
            <li>• Les états des lieux de départ et de retour restent signés par les deux parties, comme actuellement.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─── Locations (Lieux) ─────────────────────────────────────────────────
const LOC_TYPE_LABEL = { agency: "Agence", pickup: "Point de récup.", delivery: "Livraison", custom: "Personnalisé" };

function LocationsSection() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/vendor/locations")
      .then((r) => setLocations(r.data.locations || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce lieu ?")) return;
    try {
      await api.delete(`/vendor/locations/${id}`);
      setLocations(locations.filter((l) => l.id !== id));
      toast.success("Lieu supprimé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-end justify-between gap-4 mb-5">
        <div>
          <div className="overline">Lieux</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-slate-900 mt-1">Agences & points de livraison</h2>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">Ces lieux apparaissent dans les réservations (départ/retour) et dans les tâches automatiques (préparation, réception).</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700" data-testid="add-location-btn">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>
      ) : (
        <div className="space-y-2">
          {locations.map((l) => (
            <LocationRow key={l.id} location={l} onChanged={load} onDelete={() => remove(l.id)} />
          ))}
        </div>
      )}
      {showAdd && <LocationModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); toast.success("Lieu créé"); }} />}
    </div>
  );
}

function LocationRow({ location, onChanged, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [form, setForm] = useState(location);
  const ls = location.settings || {};
  const badges = [
    ls.one_way?.enabled && { label: "Aller simple", cls: "bg-amber-50 text-amber-700 border-amber-100" },
    ls.hours_enabled && { label: "Horaires propres", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
    (ls.closures || []).length > 0 && { label: `${ls.closures.length} fermeture${ls.closures.length > 1 ? "s" : ""}`, cls: "bg-slate-50 text-slate-600 border-slate-100" },
  ].filter(Boolean);
  const save = async () => {
    try {
      await api.patch(`/vendor/locations/${location.id}`, form);
      toast.success("Lieu enregistré");
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`location-row-${location.id}`}>
      {editing ? (
        <div className="space-y-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
              {Object.entries(LOC_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          </div>
          <input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs rounded-lg border border-slate-200">Annuler</button>
            <button onClick={save} className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white font-semibold">Enregistrer</button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><MapPin className="h-4 w-4 text-slate-400" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate">{location.name} <span className="text-[10px] ml-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{LOC_TYPE_LABEL[location.type] || location.type}</span></div>
            <div className="text-xs text-slate-500 truncate">{[location.address, location.city].filter(Boolean).join(", ") || "—"}</div>
            {badges.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {badges.map((b, i) => (
                  <span key={i} className={`px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${b.cls}`}>{b.label}</span>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setConfiguring((v) => !v)}
            className={`inline-flex items-center gap-1 px-3 h-8 rounded-full text-xs font-semibold transition-colors ${configuring ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
            data-testid={`configure-location-${location.id}`}
          >
            <Settings2 className="h-3 w-3" /> Configurer
          </button>
          <button onClick={() => setEditing(true)} className="text-xs font-medium text-violet-600 hover:underline" data-testid={`edit-location-${location.id}`}>Modifier</button>
          <button onClick={onDelete} className="h-8 w-8 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center" data-testid={`delete-location-${location.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {configuring && !editing && (
        <LocationSettingsPanel location={location} onSaved={() => { setConfiguring(false); onChanged(); }} />
      )}
    </div>
  );
}

function LocationModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", type: "agency", address: "", city: "", zip: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/vendor/locations", form);
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 backdrop-blur-[2px] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="location-modal">
        <div className="p-5 border-b border-slate-200 font-heading text-lg font-medium text-slate-900">Nouveau lieu</div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex: Agence Paris 8)" required className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm" data-testid="location-name" />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm" data-testid="location-type">
            {Object.entries(LOC_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm" data-testid="location-address" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ville" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm" />
            <input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} placeholder="CP" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm" />
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2">{error}</div>}
          <button type="submit" disabled={saving} className="w-full h-10 inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 text-white text-sm font-semibold disabled:opacity-50" data-testid="submit-location">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Créer
          </button>
        </form>
      </div>
    </div>
  );
}

// PaymentsSection : voir ./parametrages/PaymentsSection.jsx

// ─── Flotte (Phase 3) — Catégories, statuts custom & alertes doc ────────────
const COLOR_OPTIONS = [
  ["emerald", "Vert"], ["violet", "Violet"], ["blue", "Bleu"], ["sky", "Ciel"],
  ["amber", "Ambre"], ["red", "Rouge"], ["slate", "Gris"], ["rose", "Rose"], ["teal", "Turquoise"],
];
const COLOR_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  violet: "bg-violet-100 text-violet-700 border-violet-200",
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  sky: "bg-sky-100 text-sky-700 border-sky-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  red: "bg-red-100 text-red-700 border-red-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  teal: "bg-teal-100 text-teal-700 border-teal-200",
};

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || `custom_${Date.now()}`;

export function FleetSection() {
  const { settings, loading, reload } = useSettings();
  const [local, setLocal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState({ list: null, from: null });
  const [typesInfo, setTypesInfo] = useState(false);
  const [catsInfo, setCatsInfo] = useState(false);
  const [statusInfo, setStatusInfo] = useState(false);
  const [openType, setOpenType] = useState(null);
  const [showTypeMenu, setShowTypeMenu] = useState(false);
  const [customTypeName, setCustomTypeName] = useState("");
  const [statusesOpen, setStatusesOpen] = useState(true);
  const [guideHidden, setGuideHidden] = useState(() => localStorage.getItem("junago_fleet_guide_hidden") === "1");
  const [guideMobile, setGuideMobile] = useState(false);
  const hideGuide = () => { setGuideHidden(true); try { localStorage.setItem("junago_fleet_guide_hidden", "1"); } catch (e) {} };
  const showGuide = () => { setGuideHidden(false); try { localStorage.removeItem("junago_fleet_guide_hidden"); } catch (e) {} };

  const suggestedTypes = settings?.catalog?.suggested_types || [];
  const suggestedCatsForLabel = (label) => (suggestedTypes.find((t) => t.label === label) || {}).categories || [];

  useEffect(() => {
    if (settings?.fleet) {
      setLocal({
        categories: [...(settings.fleet.categories || [])],
        vehicle_types: [...(settings.fleet.vehicle_types || [])],
        statuses: [...(settings.fleet.statuses || [])],
        alerts: { ...(settings.fleet.alerts || {}) },
      });
    }
  }, [settings]);

  if (loading || !local) return <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-violet-600" /></div>;

  const dirty = JSON.stringify(local) !== JSON.stringify({
    categories: settings.fleet.categories || [],
    vehicle_types: settings.fleet.vehicle_types || [],
    statuses: settings.fleet.statuses || [],
    alerts: settings.fleet.alerts || {},
  });

  // id interne stable généré par Junago (indépendant du texte saisi)
  const genTypeId = () => `type_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
  const genCatId = () => `cat_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;

  const save = async () => {
    for (const t of local.vehicle_types) {
      if (!t.label?.trim()) return toast.error("Tous les types doivent avoir un nom");
    }
    // Garde-fou : pas de doublon de nom de type (insensible casse/espaces)
    const seenTypes = new Set();
    for (const t of local.vehicle_types) {
      const norm = (t.label || "").trim().toLowerCase();
      if (seenTypes.has(norm)) return toast.error(`Deux types portent le même nom : « ${t.label.trim()} ». Chaque type doit être unique.`);
      seenTypes.add(norm);
    }
    for (const c of local.categories) {
      if (!c.label?.trim()) return toast.error("Toutes les catégories doivent avoir un libellé");
    }
    for (const s of local.statuses) {
      if (!s.label?.trim()) return toast.error("Tous les statuts doivent avoir un libellé");
    }
    const removedCats = (settings.fleet.categories || []).filter((c) => !local.categories.some((x) => x.value === c.value));
    if (removedCats.length > 0 &&
        !window.confirm(`⚠️ Changement sensible : vous supprimez ${removedCats.length} catégorie(s) (${removedCats.map((c) => c.label).join(", ")}).\nLes véhicules qui l'utilisent et la grille tarifaire associée ne seront plus reliés. Continuer ?`)) return;
    setSaving(true);
    try {
      await api.put("/vendor/settings", { fleet: local });
      toast.success("Configuration de la flotte enregistrée");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  // Types de véhicules helpers — modèle 100% libre
  // Chaque nouveau type démarre avec les 5 champs communs attachés et activés (Nom interne, Marque, Modèle, Année, Couleur).
  const DEFAULT_FIELD_KEYS = ["name", "brand", "model", "year", "color"];
  const makeDefaultFields = () => ({
    catalog: Object.fromEntries(DEFAULT_FIELD_KEYS.map((k) => [k, { enabled: true }])),
    order: DEFAULT_FIELD_KEYS.map((k) => `c:${k}`),
    custom: [],
  });
  // Phase 3.2 — presets proposés à la création (Voiture est déjà présent par défaut).
  const TYPE_PRESETS = [
    { label: "Vélo", icon: "bike", color: "emerald" },
    { label: "Bateau", icon: "ship", color: "blue" },
    { label: "Moto / Scooter", icon: "bike", color: "orange" },
  ];
  const typeExists = (label) =>
    (local?.vehicle_types || []).some((t) => (t.label || "").trim().toLowerCase() === (label || "").trim().toLowerCase());
  const availablePresets = TYPE_PRESETS.filter((p) => !typeExists(p.label));
  // Création réelle du type + sa catégorie standard, uniquement après choix/saisie validé.
  const createTypeFromName = (rawLabel, preset) => {
    const label = (rawLabel || "").trim();
    if (!label) { toast.error("Saisissez un nom de type de bien"); return; }
    if (typeExists(label)) { toast.error(`Le type « ${label} » existe déjà`); return; }
    const id = genTypeId();
    const catId = genCatId();
    setLocal((p) => ({
      ...p,
      vehicle_types: [...p.vehicle_types, { value: id, label, custom: true, icon: preset?.icon || DEFAULT_TYPE_ICON, color: preset?.color || DEFAULT_TYPE_COLOR, fields: makeDefaultFields() }],
      categories: [...p.categories, { value: catId, label: `${label} standard`, type: id }],
    }));
    setOpenType(id);
    setShowTypeMenu(false);
    setCustomTypeName("");
  };
  const addSuggestedType = (sugg) => {
    const label = sugg.label;
    const id = genTypeId();
    const catId = genCatId();
    setLocal((p) => {
      if (p.vehicle_types.some((t) => t.label.toLowerCase() === label.toLowerCase())) return p;
      return {
        ...p,
        vehicle_types: [...p.vehicle_types, { value: id, label, custom: true, icon: sugg.icon || DEFAULT_TYPE_ICON, color: sugg.color || DEFAULT_TYPE_COLOR, fields: makeDefaultFields() }],
        categories: [...p.categories, { value: catId, label: `${label} standard`, type: id }],
      };
    });
    setOpenType(id);
  };
  const updateType = (val, patch) =>
    setLocal((p) => ({ ...p, vehicle_types: p.vehicle_types.map((t) => (t.value === val ? { ...t, ...patch } : t)) }));
  const removeType = async (val) => {
    const persisted = (settings.fleet.vehicle_types || []).some((t) => t.value === val);
    const t = local.vehicle_types.find((x) => x.value === val);
    const label = t?.label || "ce type";
    if (!persisted) {
      // type non encore enregistré → retrait local simple (+ ses catégories locales)
      setLocal((p) => ({
        ...p,
        vehicle_types: p.vehicle_types.filter((x) => x.value !== val),
        categories: p.categories.filter((c) => c.type !== val),
      }));
      return;
    }
    let impact;
    try {
      ({ data: impact } = await api.get(`/vendor/fleet/types/${val}/impact`));
    } catch (e) {
      return toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
    // Lot B — un type contenant des biens (actifs ou archivés) ne peut pas être supprimé
    if (impact.vehicles > 0) {
      return confirmAction({
        title: "Suppression impossible",
        message: `Suppression impossible. Ce type de bien contient actuellement ${impact.vehicles} véhicule(s). Vous devez d'abord supprimer les véhicules associés avant de pouvoir supprimer ce type.`,
        confirmLabel: "J'ai compris", variant: "warning", hideCancel: true,
      });
    }
    // Type vide → confirmation simple (ses catégories vides partent avec lui)
    if (!(await confirmAction({
      title: `Supprimer le type « ${label} » ?`,
      message: impact.categories > 0
        ? `Ce type ne contient aucun bien. Il sera supprimé avec ses ${impact.categories} catégorie(s) vide(s).`
        : "Ce type ne contient aucun bien. Il sera supprimé.",
      confirmLabel: "Supprimer", variant: "danger",
    }))) return;
    try {
      const { data } = await api.delete(`/vendor/fleet/types/${val}`);
      toast.success(data.deleted_categories ? `Type supprimé (${data.deleted_categories} catégorie(s) vide(s))` : "Type supprimé");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  // Categories helpers
  const defaultType = () => local.vehicle_types[0]?.value || null;
  const addCategory = (type) => {
    const t = type || defaultType();
    if (!t) return toast.error("Créez d'abord un type de bien");
    setLocal((p) => ({
      ...p,
      categories: [...p.categories, { value: `cat_${Math.random().toString(36).slice(2, 10)}`, label: "Nouvelle catégorie", type: t }],
    }));
  };
  const addSuggestedCategory = (type, label) => {
    setLocal((p) => {
      if (p.categories.some((c) => c.type === type && c.label.toLowerCase() === label.toLowerCase())) return p;
      return { ...p, categories: [...p.categories, { value: `cat_${Math.random().toString(36).slice(2, 10)}`, label, type }] };
    });
  };
  const updateCategory = (idx, patch) =>
    setLocal((p) => ({
      ...p,
      categories: p.categories.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  const removeCategory = async (idx) => {
    const cat = local.categories[idx];
    if (!cat) return;
    const persisted = (settings.fleet.categories || []).some((c) => c.value === cat.value);
    if (!persisted) {
      setLocal((p) => ({ ...p, categories: p.categories.filter((_, i) => i !== idx) }));
      return;
    }
    let impact;
    try {
      ({ data: impact } = await api.get(`/vendor/fleet/categories/${cat.value}/impact`));
    } catch (e) {
      return toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
    // Lot B — une catégorie contenant des biens (actifs ou archivés) ne peut pas être supprimée
    if (impact.vehicles > 0) {
      return confirmAction({
        title: "Suppression impossible",
        message: `Suppression impossible. La catégorie « ${cat.label} » contient ${impact.vehicles} véhicule(s). Vous devez d'abord supprimer les véhicules associés avant de pouvoir supprimer cette catégorie.`,
        confirmLabel: "J'ai compris", variant: "warning", hideCancel: true,
      });
    }
    if (!(await confirmAction({
      title: `Supprimer la catégorie « ${cat.label} » ?`,
      message: "Cette catégorie ne contient aucun bien. Le type parent est conservé.",
      confirmLabel: "Supprimer", variant: "danger",
    }))) return;
    try {
      await api.delete(`/vendor/fleet/categories/${cat.value}`);
      toast.success("Catégorie supprimée");
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
  };

  // Statuses helpers (idem)
  const addStatus = () => {
    const label = "Nouveau statut";
    setLocal((p) => ({
      ...p,
      statuses: [...p.statuses, { value: slugify(label + "-" + p.statuses.length), label, color: "slate" }],
    }));
  };
  const updateStatus = (idx, patch) =>
    setLocal((p) => ({ ...p, statuses: p.statuses.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
  const removeStatus = (idx) =>
    setLocal((p) => ({ ...p, statuses: p.statuses.filter((_, i) => i !== idx) }));

  const setAlert = (k, v) => setLocal((p) => ({ ...p, alerts: { ...p.alerts, [k]: v } }));

  // HTML5 drag & drop helpers
  const reorderList = (list, from, to) => {
    if (from === null || to === null || from === to) return list;
    const arr = [...list];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    return arr;
  };
  const onDragStart = (list, idx) => (e) => {
    setDragIdx({ list, from: idx });
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch (err) { /* Firefox needs setData */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (list, toIdx) => (e) => {
    e.preventDefault();
    if (dragIdx.list !== list || dragIdx.from === null) return;
    setLocal((p) => ({ ...p, [list]: reorderList(p[list], dragIdx.from, toIdx) }));
    setDragIdx({ list: null, from: null });
  };

  return (
    <div className="pb-24" data-testid="fleet-types-categories-root">
      <div className="flex items-start justify-between gap-3">
        <SectionHeader
          overline="Flotte"
          title="Configuration de la flotte"
          subtitle="Structurez votre flotte : configurez vos types de biens, leurs catégories et les champs utilisés pour vos véhicules."
        />
        <div className="shrink-0 pt-1 flex items-center gap-2">
          <button type="button" onClick={() => setGuideMobile(true)}
            className="lg:hidden inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:border-violet-300 hover:text-violet-600"
            data-testid="fleet-guide-open-mobile">
            <Info className="h-3.5 w-3.5" /> Voir le guide
          </button>
          {guideHidden && (
            <button type="button" onClick={showGuide}
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:border-violet-300 hover:text-violet-600"
              data-testid="fleet-guide-reshow">
              <Info className="h-3.5 w-3.5" /> Voir le guide
            </button>
          )}
        </div>
      </div>

      <div className="flex items-start gap-6">
        <div className="flex-1 min-w-0 max-w-3xl">

      {/* Types de bien — accordéons compacts (Type → ses catégories) */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="fleet-vehicle-types-section">
        <div className="flex items-start justify-between mb-1 gap-3">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-slate-900">Types de bien</div>
            <div className="relative">
              <button type="button" onClick={() => setTypesInfo((v) => !v)} className="text-slate-400 hover:text-violet-600" data-testid="fleet-types-info-btn" aria-label="Aide">
                <Info className="h-3.5 w-3.5" />
              </button>
              <InfoPopover open={typesInfo} onClose={() => setTypesInfo(false)} title="Type de bien"
                points={["Un type regroupe une grande famille de biens de votre flotte : Voiture, Bateau, Vélo, Moto…", "Chaque catégorie et chaque bien est rattaché à un type."]} />
            </div>
          </div>
          <div className="relative shrink-0">
            <button type="button" onClick={() => setShowTypeMenu((v) => !v)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800" data-testid="fleet-add-custom-type">
              <Plus className="h-3 w-3" /> Créer un type de bien
            </button>
            {showTypeMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => { setShowTypeMenu(false); setCustomTypeName(""); }} />
                <div className="absolute right-0 mt-1 z-40 w-64 rounded-xl border border-slate-200 bg-white shadow-lg p-1.5" data-testid="fleet-type-menu">
                  <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-400">Choisissez un type</div>
                  {availablePresets.map((p) => (
                    <button key={p.label} type="button" onClick={() => createTypeFromName(p.label, p)}
                      className="w-full text-left px-2.5 py-2 rounded-lg text-sm text-slate-700 hover:bg-violet-50 hover:text-violet-700" data-testid={`fleet-type-preset-${p.label}`}>
                      {p.label}
                    </button>
                  ))}
                  <div className="border-t border-slate-100 my-1" />
                  <div className="px-2 py-1 text-[11px] font-semibold text-slate-500">Autre</div>
                  <div className="flex items-center gap-1.5 px-1.5 pb-1.5">
                    <input value={customTypeName} onChange={(e) => setCustomTypeName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") createTypeFromName(customTypeName); }}
                      placeholder="Ex. Jet-ski, Camping-car…" className="flex-1 min-w-0 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:ring-2 focus:ring-violet-500 outline-none" data-testid="fleet-type-custom-input" />
                    <button type="button" onClick={() => createTypeFromName(customTypeName)} className="shrink-0 px-2.5 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700" data-testid="fleet-type-custom-create">Créer</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="text-xs text-slate-500 mb-4">Chaque type contient ses propres catégories. Dépliez un type pour le configurer.</div>

        {local.vehicle_types.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400 mb-3" data-testid="fleet-types-empty">
            Aucun type de bien pour le moment. Ajoutez votre premier type (ex. Voiture, Vélo, Bateau…).
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            {local.vehicle_types.map((t) => {
              const isOpen = openType === t.value;
              const cats = local.categories.map((c, idx) => ({ c, idx })).filter(({ c }) => c.type === t.value);
              const catSuggestions = suggestedCatsForLabel(t.label).filter((s) => !cats.some(({ c }) => c.label.toLowerCase() === s.toLowerCase()));
              return (
                <div key={t.value} className="rounded-xl border border-slate-200 overflow-hidden" data-testid={`fleet-type-row-${t.value}`}>
                  <button type="button" onClick={() => setOpenType(isOpen ? null : t.value)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left" data-testid={`fleet-type-accordion-${t.value}`}>
                    <span className="h-8 w-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0" style={{ color: typeColorHex(t.color) }}>
                      <TypeIcon name={t.icon} className="h-4 w-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-slate-900 truncate">{t.label || "Sans nom"}</span>
                      <span className="block text-[11px] text-slate-400">{cats.length} catégorie{cats.length > 1 ? "s" : ""}</span>
                    </span>
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: typeColorHex(t.color) }} />
                    <ChevronRight className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  </button>

                  {isOpen && (
                    <div className="p-3 sm:p-4 border-t border-slate-100 space-y-4">
                      <div className="flex flex-wrap items-end gap-3">
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-slate-500">Nom du type</span>
                          <input value={t.label} onChange={(e) => updateType(t.value, { label: e.target.value })}
                            className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm w-full max-w-[220px]" placeholder="Ex. Voiture"
                            data-testid={`fleet-type-label-${t.value}`} />
                        </label>
                        <button type="button" onClick={() => removeType(t.value)}
                          className="h-9 px-3 rounded-md border border-slate-200 text-xs text-slate-500 hover:bg-red-50 hover:text-red-600 inline-flex items-center gap-1.5 ml-auto" data-testid={`fleet-type-remove-${t.value}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Supprimer
                        </button>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-slate-500 mb-1.5">Icône</div>
                        <div className="flex flex-wrap gap-1.5" data-testid={`fleet-type-icon-${t.value}`}>
                          {ICON_PICKER.map((ic) => (
                            <button key={ic} type="button" onClick={() => updateType(t.value, { icon: ic })}
                              className={`h-8 w-8 rounded-lg border flex items-center justify-center transition-colors ${t.icon === ic ? "border-violet-500 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                              <TypeIcon name={ic} className="h-4 w-4" />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-[11px] font-medium text-slate-500 mb-1.5">Couleur</div>
                        <div className="flex flex-wrap items-center gap-1.5" data-testid={`fleet-type-color-${t.value}`}>
                          {TYPE_COLOR_OPTIONS.map(([val]) => (
                            <button key={val} type="button" onClick={() => updateType(t.value, { color: val })} aria-label={val}
                              className={`h-7 w-7 rounded-full flex items-center justify-center border-2 ${t.color === val ? "border-slate-900" : "border-transparent"}`}>
                              <span className="h-4 w-4 rounded-full" style={{ backgroundColor: typeColorHex(val) }} />
                            </button>
                          ))}
                          <label className={`relative h-7 w-7 rounded-full flex items-center justify-center border-2 cursor-pointer ${isCustomColor(t.color) ? "border-slate-900" : "border-transparent"}`}
                            title="Couleur personnalisée" data-testid={`fleet-type-color-custom-${t.value}`}>
                            <span className="h-4 w-4 rounded-full flex items-center justify-center text-white"
                              style={{ background: isCustomColor(t.color) ? t.color : "conic-gradient(from 0deg, #ef4444, #f59e0b, #10b981, #3b82f6, #8b5cf6, #ef4444)" }}>
                              {!isCustomColor(t.color) && <Plus className="h-2.5 w-2.5" />}
                            </span>
                            <input type="color" value={isCustomColor(t.color) ? t.color : "#1e73be"} onChange={(e) => updateType(t.value, { color: e.target.value })}
                              className="absolute inset-0 opacity-0 cursor-pointer" data-testid={`fleet-type-color-picker-${t.value}`} />
                          </label>
                          {isCustomColor(t.color) && (
                            <input value={t.color} onChange={(e) => { let v = e.target.value; if (v && !v.startsWith("#")) v = "#" + v; updateType(t.value, { color: v }); }}
                              maxLength={7} placeholder="#1E73BE"
                              className="h-7 w-24 px-2 rounded-md border border-slate-200 bg-white text-[11px] font-mono text-slate-600 uppercase"
                              data-testid={`fleet-type-color-hex-${t.value}`} />
                          )}
                        </div>
                      </div>

                      <div data-testid={`fleet-cat-group-${t.value}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Catégories</span>
                          <div className="relative">
                            <button type="button" onClick={() => setCatsInfo((v) => (v === t.value ? null : t.value))} className="text-slate-400 hover:text-violet-600" aria-label="Aide catégories">
                              <Info className="h-3.5 w-3.5" />
                            </button>
                            <InfoPopover open={catsInfo === t.value} onClose={() => setCatsInfo(null)} title="Catégorie"
                              points={["Une catégorie organise les biens à l'intérieur d'un type.", "Exemple : Voiture → Compacte → Renault Clio."]} />
                          </div>
                        </div>
                        {cats.length === 0 ? (
                          <div className="rounded-lg border border-dashed border-slate-200 py-3 text-center text-xs text-slate-400 mb-2">Aucune catégorie créée.</div>
                        ) : (
                          <div className="flex flex-col gap-2 mb-2">
                            {cats.map(({ c, idx }) => (
                              <div key={c.value} className="flex items-center gap-2" data-testid={`fleet-category-${idx}`}>
                                <input value={c.label} onChange={(e) => updateCategory(idx, { label: e.target.value })}
                                  className="h-9 px-2 rounded-md border border-slate-200 bg-white text-sm w-full max-w-[280px]" placeholder="Libellé"
                                  data-testid={`fleet-category-label-${idx}`} />
                                <button type="button" onClick={() => removeCategory(idx)}
                                  className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center shrink-0" data-testid={`fleet-category-remove-${idx}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {catSuggestions.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`fleet-cat-suggestions-${t.value}`}>
                            {catSuggestions.map((s) => (
                              <button key={s} type="button" onClick={() => addSuggestedCategory(t.value, s)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 text-[11px] hover:border-violet-400 hover:text-violet-600" data-testid={`fleet-cat-suggestion-${t.value}-${slugify(s)}`}>
                                <Plus className="h-2.5 w-2.5" /> {s}
                              </button>
                            ))}
                          </div>
                        )}
                        <button type="button" onClick={() => addCategory(t.value)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50" data-testid={`fleet-add-category-${t.value}`}>
                          <Plus className="h-3 w-3" /> Ajouter une catégorie
                        </button>
                      </div>
                      <TypeFieldsConfig
                        typeValue={t.value}
                        typeLabel={t.label}
                        categories={cats.map(({ c }) => c.label)}
                        fields={t.fields}
                        fieldCatalog={settings?.catalog?.field_catalog || []}
                        onChange={(fields) => updateType(t.value, { fields })}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Suggestions de types (max 5) — chips uniquement, création au clic */}
        {suggestedTypes.filter((s) => !local.vehicle_types.some((t) => t.label.toLowerCase() === s.label.toLowerCase())).length > 0 && (
          <div data-testid="fleet-type-suggestions">
            <div className="text-[11px] text-slate-400 mb-1.5">Suggestions — un clic crée le type (vous ajoutez ses catégories ensuite) :</div>
            <div className="flex flex-wrap gap-1.5">
              {suggestedTypes.filter((s) => !local.vehicle_types.some((t) => t.label.toLowerCase() === s.label.toLowerCase())).map((s) => (
                <button key={s.label} type="button" onClick={() => addSuggestedType(s)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-dashed border-slate-300 text-slate-600 text-[11px] hover:border-violet-400 hover:text-violet-600" data-testid={`fleet-type-suggestion-${slugify(s.label)}`}>
                  <TypeIcon name={s.icon} className="h-3 w-3" /> {s.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Statuts */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="fleet-statuses-section">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-2 min-w-0">
            <button type="button" onClick={() => setStatusesOpen((o) => !o)} className="flex items-start gap-2 text-left min-w-0" data-testid="fleet-statuses-toggle">
              <ChevronRight className={`h-4 w-4 text-slate-400 mt-0.5 shrink-0 transition-transform ${statusesOpen ? "rotate-90" : ""}`} />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-slate-900">Statuts personnalisés</span>
                <span className="block text-xs text-slate-500 mt-0.5">Ajoutez des statuts propres à votre activité (ex : "En essais", "En livraison"…).</span>
              </span>
            </button>
            <div className="relative">
              <button type="button" onClick={() => setStatusInfo((v) => !v)} className="text-slate-400 hover:text-violet-600" data-testid="fleet-status-info-btn" aria-label="Aide">
                <Info className="h-3.5 w-3.5" />
              </button>
              <InfoPopover open={statusInfo} onClose={() => setStatusInfo(false)} title="Statut personnalisé"
                points={["Les statuts reflètent l'état opérationnel d'un bien : Disponible, Loué, Réservé, Nettoyage, Maintenance, Hors service…", "Ajoutez vos propres statuts pour compléter ces états par défaut selon votre activité.", "Le statut s'affiche sur la fiche du bien et aide à suivre sa disponibilité au quotidien."]} />
            </div>
          </div>
          {statusesOpen && (
            <button
              type="button"
              onClick={addStatus}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 shrink-0"
              data-testid="fleet-add-status"
            >
              <Plus className="h-3 w-3" /> Ajouter
            </button>
          )}
        </div>
        {statusesOpen && (local.statuses.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400">Aucun statut.</div>
        ) : (
          <div className="space-y-2">
            {local.statuses.map((s, idx) => (
              <div
                key={s.value + idx}
                className={`flex items-center gap-2 p-2 rounded-xl border bg-slate-50 transition-colors ${dragIdx.list === "statuses" && dragIdx.from === idx ? "opacity-40 border-violet-400" : "border-slate-200 hover:border-slate-300"}`}
                draggable
                onDragStart={onDragStart("statuses", idx)}
                onDragOver={onDragOver}
                onDrop={onDrop("statuses", idx)}
                data-testid={`fleet-status-${idx}`}
              >
                <GripVertical className="h-4 w-4 text-slate-300 shrink-0 cursor-grab active:cursor-grabbing" />
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${COLOR_CLASSES[s.color] || COLOR_CLASSES.slate}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current" /> {s.label || "…"}
                </span>
                <input
                  value={s.label}
                  onChange={(e) => updateStatus(idx, { label: e.target.value })}
                  className="flex-1 h-9 px-2 rounded-md border border-slate-200 bg-white text-sm"
                  placeholder="Libellé"
                  data-testid={`fleet-status-label-${idx}`}
                />
                <select
                  value={s.color || "slate"}
                  onChange={(e) => updateStatus(idx, { color: e.target.value })}
                  className="h-9 px-2 rounded-md border border-slate-200 bg-white text-xs"
                  data-testid={`fleet-status-color-${idx}`}
                >
                  {COLOR_OPTIONS.map(([val, lab]) => (
                    <option key={val} value={val}>{lab}</option>
                  ))}
                </select>
                <input
                  value={s.value}
                  onChange={(e) => updateStatus(idx, { value: slugify(e.target.value) })}
                  className="w-32 h-9 px-2 rounded-md border border-slate-200 bg-white text-xs font-mono text-slate-500"
                  placeholder="Clé"
                  data-testid={`fleet-status-value-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeStatus(idx)}
                  className="h-8 w-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center shrink-0"
                  data-testid={`fleet-status-remove-${idx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>

        </div>

        {!guideHidden && (
          <aside className="hidden lg:block w-[340px] shrink-0 sticky top-4" data-testid="fleet-guide-aside">
            <FleetOnboardingGuide onHide={hideGuide} />
          </aside>
        )}
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save} testid="fleet-save" />

      {guideMobile && (
        <div className="fixed inset-0 z-50 lg:hidden flex items-end sm:items-center justify-center" data-testid="fleet-guide-mobile-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setGuideMobile(false)} />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
            <FleetOnboardingGuide onHide={() => setGuideMobile(false)} embedded={false} />
          </div>
        </div>
      )}
    </div>
  );
}
